import React, { useState } from 'react';
import { Check, Reply, Trash2, Edit2, CheckCheck, Heart, X } from 'lucide-react';
import type { Message } from '../../types/chat';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useTheme } from '@/contexts/ThemeContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { isEmoji } from '@/lib/utils/emoji';

interface EditingState {
  messageId: string | null;
  content: string;
}

interface MessageGroupProps {
  date: string;
  messages: Message[];
  conversation?: {
    name: string;
    image: string;
  };
  onDelete?: (messageId: string) => void;
  onEdit?: (messageId: string, newContent: string) => void;
  onReply?: (message: Message) => void;
  onLike?: (messageId: string) => void;
  editingId?: string | null;
  onStartEdit?: (messageId: string | null) => void;
}

const MessageGroup: React.FC<MessageGroupProps> = ({
  date,
  messages,
  conversation,
  onDelete,
  onEdit,
  onReply,
  onLike,
  editingId,
  onStartEdit
}) => {
  const { isDarkMode } = useTheme();
  const [editContent, setEditContent] = useState('');
  const [localEditingState, setLocalEditingState] = useState<EditingState>({
    messageId: null,
    content: ''
  });
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const navigate = useNavigate();


  const isImageUrl = (url: string): boolean =>
    /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url);

  // Enhanced content formatter to handle emojis and images
    // Enhanced content formatter to handle emojis and images
  const formatContent = (content: string, message: Message) => {
    // **1st**: if an image URL is present, render it
    if (message.image) {
      return (
        <img
          src={message.image}
          alt="Shared image"
          className="w-full h-auto rounded-lg max-w-[300px]"
          loading="lazy"
        />
      );
    }

    // **2nd**: only if there was no image, check for single-emoji
    if (isEmoji(content) && content.trim().length <= 4) {
      return (
        <span className="text-4xl leading-none block py-2">{content}</span>
      );
    }

    // …the rest of your link/markdown parsing…

    
    const parts = content.split(
      /(\*\*.*?\*\*|\bhttps?:\/\/\S+\b|\[Profile\]\([^)]+\)|\[Image\]\([^)]+\)|\[Avatar\]\([^)]+\))/g
    );
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('[Profile](')) {
        const url = part.match(/\(([^)]+)\)/)?.[1] || '';
        return (
          <button
            key={i}
            onClick={() => navigate(url)}
            className="text-xs px-3 py-1 rounded-full bg-gold-400/20 text-gold-400"
          >
            View Profile
          </button>
        );
      }
      if (part.startsWith('[Avatar](') || part.startsWith('[Image](')) {
        const url = part.match(/\(([^)]+)\)/)?.[1];
        if (url) {
          return (
            <img
              key={i}
              src={url}
              alt="Image"
              className="w-full h-auto rounded-lg mt-2 max-w-[300px]"
              loading="lazy"
            />
          );
        }
      }
      if (/^https?:\/\//.test(part)) {
        if (isImageUrl(part)) {
          return (
            <img
              key={i}
              src={part}
              alt="Image"
              className="w-full h-auto rounded-lg mt-2 max-w-[300px]"
              loading="lazy"
            />
          );
        }
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1 rounded-full bg-dark-400/50 text-white"
          >
            Open Link
          </a>
        );
      }
      return part;
    });
  };

  const handleStartEdit = (message: Message) => {
    // Store the full message content in local state
    setLocalEditingState({
      messageId: message.id,
      content: message.content
    });
    if (onStartEdit) {
      onStartEdit(message.id);
    }
  };

  const handleCancelEdit = () => {
    setLocalEditingState({
      messageId: null,
      content: ''
    });
    if (onStartEdit) {
      onStartEdit(null);
    }
  };

  const handleSaveEdit = (messageId: string) => {
    if (localEditingState.content.trim() && onEdit) {
      onEdit(messageId, localEditingState.content);
    }
  };

  const handleDelete = (messageId: string) => {
    if (isDeleting === messageId) {
      // Confirm delete
      if (onDelete) {
        onDelete(messageId);
      }
      setIsDeleting(null);
    } else {
      // First click - show confirmation
      setIsDeleting(messageId);
      // Auto-reset after 3 seconds
      setTimeout(() => setIsDeleting(null), 3000);
    }
  };

  const getStatusIcon = (status?: 'sent' | 'delivered' | 'read') => {
    switch (status) {
      case 'sent':
        return <Check className="h-3 w-3 text-dark-800/70" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-dark-800/70" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-gold-400" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center my-3">
        <div className="px-4 py-1.5 text-xs rounded-full shadow-sm bg-gray-100/70 text-gray-600 dark:bg-dark-300/70 dark:text-dark-800 border border-gray-200/30 dark:border-dark-400/30">
          {date}
        </div>
      </div>

      {messages.map((message) => (
        <motion.div
          key={message.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.3,
            ease: "easeOut"
          }}
          className={cn(
            'w-full flex items-end space-x-2 mb-3',
            message.isSender ? 'justify-end' : 'justify-start'
          )}
        >
          {!message.isSender && (
            <img
              src={conversation?.image}
              alt="avatar"
              className="w-8 h-8 rounded-full object-cover border border-dark-400"
            />
          )}

          <div className="group relative max-w-[75%] min-w-0">
            {/* Action buttons - now positioned to the left of messages */}
            <div className={cn(
              'absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all',
              message.isSender ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]',
              'z-10'
            )}>
              <div className={cn(
                "flex flex-col items-center space-y-2 p-1 rounded-lg border backdrop-blur-sm shadow-lg",
                isDarkMode 
                  ? "bg-dark-300/95 border-dark-400/30" 
                  : "bg-white/95 border-gray-200/50"
              )}>
                {onReply && (
                  <button
                    onClick={() => onReply(message)}
                    className={cn(
                      "p-2 rounded-lg transition-colors",
                      isDarkMode
                        ? "text-dark-800 hover:bg-dark-400 hover:text-white"
                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    )}
                  >
                   <Reply className="h-5 w-5" />
                  </button>
                )}
                {message.isSender && onEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); 
                      handleStartEdit(message);
                    }}
                    className={cn(
                     "p-2 rounded-lg transition-colors",
                      isDarkMode
                        ? "text-dark-800 hover:bg-dark-400 hover:text-white"
                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    )}
                  >
                   <Edit2 className="h-5 w-5" />
                  </button>
                )}
                {message.isSender && onDelete && (
                  <button
                    onClick={() => handleDelete(message.id)}
                    className={cn("p-2 rounded-lg transition-colors", 
                      isDeleting === message.id
                        ? "bg-red-500/10 text-red-500"
                        : isDarkMode
                          ? "text-dark-800 hover:bg-dark-400 hover:text-red-400"
                          : "text-gray-500 hover:bg-gray-100 hover:text-red-400"
                    )}
                  >
                    {isDeleting === message.id ? (
                     <span className="text-sm font-medium whitespace-nowrap px-2 py-1">Confirm?</span>
                    ) : (
                     <Trash2 className="h-5 w-5" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {(message.reply_to_message || message.replyTo) && (
              <div className={cn(
                'mb-2 p-2 rounded-lg text-sm',
                message.isSender 
                  ? 'dark:bg-gold-500/20 dark:text-gold-400 light:bg-gold-100 light:text-gold-700' 
                  : 'dark:bg-dark-400/50 dark:text-white light:bg-gray-200 light:text-gray-800'
              )}>
                <div className="flex items-center text-dark-800 space-x-2 mb-1">
                  <Reply className="h-3.5 w-3.5 dark:text-dark-800 light:text-gray-500" />
                  <span className="text-xs dark:text-dark-800 light:text-gray-500">Replying to</span>
                </div>
                <div className="pl-5 text-xs dark:text-inherit light:text-gray-700">
                  {message.reply_to_message?.content || message.replyTo?.content || ''}
                </div>
              </div>
            )}

            <div
              className={cn(
                'px-4 py-2.5 transition-all shadow-sm hover:shadow-md',
                message.isSender
                  ? 'bg-gradient-to-br from-gold-400 to-gold-500 text-dark-50 rounded-t-2xl rounded-l-2xl border border-gold-300/20'
                  : 'bg-white dark:bg-dark-300 text-gray-900 dark:text-white rounded-t-2xl rounded-r-2xl border border-gray-200 dark:border-dark-400 backdrop-blur-sm'
              )}
            >
              {editingId === message.id ? (
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <textarea
                      value={localEditingState.content}
                      onChange={(e) => setLocalEditingState(prev => ({ ...prev, content: e.target.value }))}
                      className={cn(
                        "w-full rounded-lg px-3 py-2 text-sm min-h-[100px] resize-y",
                        isDarkMode 
                          ? "bg-dark-400 text-white focus:ring-1 focus:ring-gold-400/50 focus:outline-none" 
                          : "bg-gray-100 text-gray-900 focus:ring-1 focus:ring-gold-600/50 focus:outline-none"
                      )}
                      autoFocus
                      style={{ maxHeight: '200px' }}
                    ></textarea>
                  </div>
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => handleSaveEdit(message.id)}
                      className={cn(
                       "p-2.5 rounded-lg",
                        isDarkMode 
                          ? "hover:bg-dark-500 text-green-400 hover:text-green-300" 
                          : "hover:bg-gray-200 text-green-600 hover:text-green-500"
                      )}
                      title="Save"
                    >
                     <Check className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className={cn(
                       "p-2.5 rounded-lg",
                        isDarkMode 
                          ? "hover:bg-dark-500 text-red-400 hover:text-red-300" 
                          : "hover:bg-gray-200 text-red-600 hover:text-red-500"
                      )}
                      title="Cancel"
                    >
                     <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed break-words">
                    {formatContent(message.content, message)}
                  </p>
                  <div className="flex items-center justify-end space-x-2 mt-1 text-right">
                    <span className={cn(
                      'text-[10px]',
                      message.isSender
                        ? 'text-dark-50/80'
                        : isDarkMode
                        ? 'text-dark-800'
                        : 'text-light-800'
                    )}>
                      {format(new Date(message.time), 'HH:mm')}
                    </span>

                    {message.isSender && message.likesCount > 0 && (
                      <div className="absolute -bottom-3 -left-3 z-10">
                        <div className="relative flex items-center justify-center bg-dark-200 dark:bg-dark-200 light:bg-white rounded-full p-1 shadow-sm border border-dark-400/30 dark:border-dark-400/30 light:border-gray-200/50">
                          <div className="relative">
                            <svg 
                              width="16" 
                              height="16" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              className="text-red-500 fill-current animate-pulse relative z-10"
                            >
                              <path 
                                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" 
                              />
                            </svg>
                            <span className="heart-beat" />
                          </div>
                        </div>
                      </div>
                    )}

                    {!message.isSender && onLike && (
                     <button
  onClick={(e) => {
    e.stopPropagation();
    if (onLike) {
      onLike(message.id);
    }
  }}
  className="absolute -bottom-3 -left-3 z-10 transition-transform hover:scale-125 active:scale-90 cursor-pointer"
  title={message.isLiked ? 'Unlike' : 'Like'}
>
  <div className="relative flex items-center justify-center bg-dark-200 dark:bg-dark-200 light:bg-white rounded-full p-1 shadow-sm border border-dark-400/30 dark:border-dark-400/30 light:border-gray-200/50">
    <div className="relative">
      <svg 
        width="16" 
        height="16" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        className={cn(
          "transition-all duration-300 transform relative z-10",
          message.isLiked
            ? "text-red-500 fill-red-500"
            : isDarkMode
              ? "text-dark-800 hover:text-red-500"
              : "text-gray-400 hover:text-red-500"
        )}
      >
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
      </svg>
      {message.isLiked && <span className="heart-beat absolute" />}
    </div>
  </div>
</button>

                    )}

                    {message.isSender && getStatusIcon(message.status)}
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default MessageGroup;